import { useCallback } from 'react';
import { ExtendedClientFormData } from '../types';
import { Scheme, ClientCategory, ClientGroup, ClientClass, Distributor, Company } from '@/types/client';
import { isNumericField } from '../utils/fieldUtils';
import styles from '../ClientCommon.module.css';

interface UseApprovalReviewLogicProps {
    isApprovalMode: boolean;
    changedFields: Record<string, boolean>;
    originalClientData: ExtendedClientFormData | null;
    schemes: Scheme[];
    categories: ClientCategory[];
    groups: ClientGroup[];
    clientClasses: ClientClass[];
    distributors: Distributor[];
    countries: Company[]; // Assuming Company type can represent countries if 'name' and 'id' are present
    companies?: Array<{ id: number; label: string; name?: string }>;
}


export const useApprovalReviewLogic = ({
    isApprovalMode,
    changedFields,
    originalClientData,
    schemes,
    categories,
    groups,
    clientClasses,
    distributors,
    countries,
    companies,
}: UseApprovalReviewLogicProps) => {

    const renderPreviousValue = useCallback((fieldName: string) => {
        if (!isApprovalMode || !changedFields[fieldName] || !originalClientData) {
            return null;
        }

        const previousValue = originalClientData[fieldName];
        let displayValue = '';

        if (previousValue === null || previousValue === undefined || (typeof previousValue === 'string' && previousValue.trim() === '')) {
            displayValue = 'EMPTY';
        } else if (typeof previousValue === 'boolean') {
            displayValue = previousValue ? 'YES' : 'NO';
        } else if (Array.isArray(previousValue)) {
            // Special handling for restricted_companies (array of IDs)
            if (fieldName === 'restricted_companies' && companies) {
                displayValue = previousValue
                    .map(id => {
                        const company = companies.find(c => c.id === id);
                        return company ? (company.name || company.label) : String(id);
                    })
                    .join(', ');
            } else {
                displayValue = previousValue.map(v => (v && typeof v === 'object' ? (v.name || v.symbol || v.id) : String(v))).join(', ');
            }
        } else if (fieldName.includes('date') && previousValue) {
            try {
                displayValue = new Date(previousValue as string).toLocaleDateString('en-GB'); // DD/MM/YYYY
            } catch (e) {
                displayValue = String(previousValue);
            }
        } else if (typeof previousValue === 'object') {
            // Handle object representation for related fields (e.g., scheme, category)
            displayValue = (previousValue as any).name || (previousValue as any).group_name || (previousValue as any).label || (previousValue as any).full_name || String((previousValue as any).id || '');
        } else if (isNumericField(fieldName)) {
            // It's a primitive ID, try to find its name in lookups
            const id = Number(previousValue);
            let foundName = '';

            if (fieldName === 'scheme') foundName = schemes.find(s => s.id === id)?.name || '';
            else if (fieldName === 'category') foundName = categories.find(c => c.id === id)?.name || '';
            else if (fieldName === 'group') foundName = groups.find(g => g.id === id)?.group_name || groups.find(g => g.id === id)?.name || '';
            else if (fieldName === 'client_class') foundName = clientClasses.find(c => c.id === id)?.name || '';
            else if (fieldName === 'distributor') foundName = distributors.find(d => d.id === id)?.name || '';
            else if (fieldName === 'country_id' || fieldName === 'country') foundName = countries.find(c => c.id === id)?.name || '';

            const isMoney = ['block_amount', 'cash_threshold_limit'].includes(fieldName);
            if (isMoney) {
                displayValue = `₹${id.toLocaleString()}`;
            } else {
                displayValue = foundName || String(id);
            }
        } else {
            displayValue = String(previousValue);
        }

        return <span className={styles['previous-value']}>Previously: {displayValue}</span>;
    }, [isApprovalMode, changedFields, originalClientData, schemes, categories, groups, clientClasses, distributors, countries, companies]);

    return { renderPreviousValue };
};
